"use client"

import { buildExcerpt } from '@/lib/blog-excerpt'

import Link from "next/link"
import { CardImage } from "@/components/brand-kit/card-image"
import { alternateCardImage } from "@/lib/card-image"
import { Breadcrumb } from "@/components/brand-kit/breadcrumb"
import { hubBreadcrumbs } from "@/lib/breadcrumbs"
import { ArticleCard } from "@/components/brand-kit/article-card"
import { ToolLinkCard } from "@/components/brand-kit/tool-link-card"
import { FAQAccordion } from "@/components/brand-kit/faq-accordion"
import { ProductCarouselCard } from "@/components/brand-kit/product-carousel-card"
import { ProductShareButtons } from "@/components/blocks/product-share-buttons"
import {
  PAGE_W,
  transformProducts, toCarouselProduct, getProductUrl,
  getFeaturedProducts, getTopRatedProducts, getBestValueProducts,
  formatPrice, formatDate, formatProductTitle, stripBestPrefix,
  PageSectionHeader, ScrollCarousel, StarRating,
  hubHeroIntro,
  type HubClientProps,
  hubCollectionHref,
  hubBrandHref,
  MaybeLink,
  HubHeroImage,
} from "./hub-shared"
import { productCardCtaName } from "@/lib/card-link-names"
import { dealCtaLabel, hasRenderedPrice } from "@/lib/deal-cta"
import { OptimizedImg } from "@/components/ui/optimized-img"

export default function FeatureHub({ hub, products, articles, relatedHubs = [] }: HubClientProps) {
  const collectionSlug = hub.collection_slug || hub.slug
  const collectionHref = hubCollectionHref(hub) // FRIDGE-1119: null when the collection does not render
  const displayProducts = transformProducts(products)
  const featuredProducts = getFeaturedProducts(displayProducts)
  const topRatedProducts = getTopRatedProducts(displayProducts)
  const bestValueProducts = getBestValueProducts(displayProducts)

  return (
    <div>
      <div className={`${PAGE_W} py-8`}>
        {/* ONE trail (lib/breadcrumbs.ts) — page.tsx emits the same hubBreadcrumbs(hub) as the BreadcrumbList (FRIDGE-269) */}
        <Breadcrumb trail={hubBreadcrumbs(hub)} />

        {/* Hero Banner — Feature Spotlight */}
        <section className="mb-12 rounded-2xl overflow-hidden border border-gray-100 shadow-[0_4px_24px_rgba(0,25,69,0.06)]">
          {hub.featured_image ? (
            <div className="grid grid-cols-1 lg:grid-cols-2 min-h-[360px]">
              <div className="relative bg-gray-100 min-h-[240px] lg:min-h-full">
                <HubHeroImage src={hub.featured_image} alt={`${hub.title} | Fridge.com`} />
                <div className="absolute inset-0 bg-gradient-to-r from-black/5 to-black/20" />
              </div>
              <div className="flex flex-col justify-center p-8 lg:p-10 bg-white">
                <span className="text-[#ff9951] font-bold tracking-widest text-xs mb-3 block uppercase">Feature Spotlight</span>
                <h1 className="text-3xl md:text-4xl font-black tracking-tighter leading-tight text-gray-900 mb-3">{hub.title}</h1>
                <p className="text-gray-500 text-sm leading-relaxed mb-6 max-w-md">{hubHeroIntro(hub)}</p>{/* first sentence — keyword-placement standard (plan item 15) */}
                {collectionHref ? (<Link href={collectionHref} className="inline-flex items-center gap-2 self-start bg-[#ff9951] hover:bg-[#ff8533] text-[var(--color-brand-action-text)] text-sm font-semibold px-5 py-2.5 rounded-lg transition-colors">
                  Browse {hub.title}
                  <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 5l7 7-7 7" /></svg>
                </Link>) : null}
              </div>
            </div>
          ) : (
            <div className="py-12 px-8 lg:px-12 bg-gradient-to-br from-white to-gray-50">
              <span className="text-[#ff9951] font-bold tracking-widest text-xs mb-3 block uppercase">Feature Spotlight</span>
              <h1 className="text-3xl md:text-4xl font-black tracking-tighter leading-tight text-gray-900 mb-3">{hub.title}</h1>
              <p className="text-gray-500 text-sm leading-relaxed mb-6 max-w-lg">{hubHeroIntro(hub)}</p>{/* first sentence — keyword-placement standard (plan item 15) */}
              {collectionHref ? (<Link href={collectionHref} className="inline-flex items-center gap-2 bg-[#ff9951] hover:bg-[#ff8533] text-[var(--color-brand-action-text)] text-sm font-semibold px-5 py-2.5 rounded-lg transition-colors">
                Browse {hub.title}
                <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 5l7 7-7 7" /></svg>
              </Link>) : null}
            </div>
          )}
        </section>

        <div className="mb-6">
          <ProductShareButtons productTitle={hub.title} pageType="guide" variant="top" />
        </div>

        {/* ─── Featured Products Grid ─────────────────────────────────── */}
        {displayProducts.length > 0 && (
          <section className="mb-12">
            <PageSectionHeader
              eyebrow="Top Models"
              title={`Best ${stripBestPrefix(hub.title)}`}
              actions={
                collectionHref ? (<Link href={collectionHref} className="tap-24 text-sm font-medium text-[#0469f0] hover:underline flex items-center gap-1">
                  View All
                  <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 5l7 7-7 7" /></svg>
                </Link>) : undefined
              }
            />
            <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-4 gap-4">
              {displayProducts.slice(0, 8).map((product, idx) => {
                const productUrl = getProductUrl(product)
                // FRIDGE-429 — ONE boolean gates the price row AND picks the CTA
                // label, read from the very column the price element renders.
                const showsPrice = hasRenderedPrice(product.lowest_price_cents)
                return (
                  <div key={product.id} className="group bg-white rounded-xl border border-gray-100 hover:border-[#0469f0]/20 hover:shadow-[0_4px_20px_rgba(0,25,69,0.06)] transition-all overflow-hidden flex flex-col">
                    <div className="relative">
                      <div className="aspect-square bg-white overflow-hidden">
                        <CardImage src={product.images[0] || ''} alternate={alternateCardImage(product.images[0] || '', product.images)} href={productUrl} alt={`${product.title} | Fridge.com`} width={300} height={300} priority={idx < 3} />
                      </div>
                    </div>
                    <div className="p-4 border-t border-gray-50 flex flex-col flex-grow">
                      <Link href={productUrl}>
                        <span className="text-[10px] font-bold uppercase tracking-widest text-[#0469f0] mb-1 block">{product.brand}</span>
                        <h3 className="text-sm font-semibold text-gray-900 leading-snug mb-2 line-clamp-3 group-hover:text-[#0469f0] transition-colors">{formatProductTitle(product.title)}</h3>
                      </Link>
                      {showsPrice && (
                        <div className="flex items-baseline gap-2 mb-1">
                          <span className="text-lg font-black text-gray-900">{formatPrice(product.lowest_price_cents)}</span>
                        </div>
                      )}
                      <div className="mb-3">
                        <StarRating rating={product.rating} />
                      </div>
                      <Link href={productUrl} aria-label={productCardCtaName(dealCtaLabel(showsPrice), product, productUrl)} className="mt-auto w-full bg-[#ff9951] hover:bg-[#ff8533] text-[var(--color-brand-action-text)] text-sm font-bold py-2.5 rounded-lg transition-all active:scale-95 flex items-center justify-center gap-2">
                        {dealCtaLabel(showsPrice)}
                        <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M7 17L17 7M17 7H7M17 7v10" /></svg>
                      </Link>
                    </div>
                  </div>
                )
              })}
            </div>
          </section>
        )}

        {/* ─── Explore Related Categories ─────────────────────────────── */}
        {relatedHubs.length > 0 && (
          <section className="mb-12">
            <PageSectionHeader eyebrow="Discover More" title="Explore Related Categories" />
            <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-3">
              {relatedHubs.slice(0, 5).map((rh) => (
                <ToolLinkCard key={rh.slug} title={rh.title} description="Browse collection" href={`/${rh.slug}`} />
              ))}
            </div>
          </section>
        )}
      </div>

      {/* ─── Carousel: Top-Rated ─────────────────────────────────────── */}
      {topRatedProducts.length > 0 && (
        <ScrollCarousel eyebrow="Highest Rated" title={`Top-Rated ${stripBestPrefix(hub.title)}`} bgClass="bg-gray-50">
          {topRatedProducts.map((p) => (
            <ProductCarouselCard key={p.id} product={toCarouselProduct(p)} />
          ))}
        </ScrollCarousel>
      )}

      {/* ─── Brands ──────────────────────────────────────────────────── */}
      {hub.brands_section && hub.brands_section.length > 0 && (
        <div className={PAGE_W}>
          <section className="my-12">
            <PageSectionHeader eyebrow="Top Brands" title={`${stripBestPrefix(hub.title)} by Brand`} />
            <div className="bg-gray-50 rounded-xl p-6">
              <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-4">
                {hub.brands_section.map((brand) => (
                  <MaybeLink key={brand.name} href={hubBrandHref(brand)} className="group bg-white rounded-lg p-5 border border-gray-100 hover:border-[#ff9951]/30 hover:shadow-[0_4px_20px_rgba(0,25,69,0.06)] transition-all">
                    <div className="flex items-center gap-3 mb-3">
                      {brand.logo ? (
                        <img src={brand.logo} alt={`${brand.name} logo`} className="w-8 h-8 object-contain flex-shrink-0" />
                      ) : (
                        <div className="w-8 h-8 rounded-full bg-gray-100 flex items-center justify-center flex-shrink-0">
                          <span className="text-xs font-bold text-gray-500">{brand.name.charAt(0)}</span>
                        </div>
                      )}
                      <h3 className="font-bold text-gray-900">{brand.name}</h3>
                    </div>
                    <ul className="space-y-2">
                      {brand.features.map((feature, i) => (
                        <li key={i} className="flex items-start gap-2">
                          <svg className="w-4 h-4 text-[#ff9951] flex-shrink-0 mt-0.5" fill="none" stroke="currentColor" viewBox="0 0 24 24"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 12l2 2 4-4m6 2a9 9 0 11-18 0 9 9 0 0118 0z" /></svg>
                          <span className="text-xs text-gray-600 leading-snug">{feature}</span>
                        </li>
                      ))}
                    </ul>
                  </MaybeLink>
                ))}
              </div>
            </div>
          </section>
        </div>
      )}

      {/* ─── FAQ ──────────────────────────────────────────────────────── */}
      {hub.faqs && hub.faqs.length > 0 && (
        <div className={`${PAGE_W} py-8`}>
          <PageSectionHeader eyebrow="Common Questions" title="Frequently Asked Questions" />
          <FAQAccordion items={hub.faqs} defaultOpen={[0]} columns={1} title="" bordered={false} className="mt-0" />
        </div>
      )}

      {/* ─── Carousel: Best Value ────────────────────────────────────── */}
      {bestValueProducts.length > 0 && (
        <ScrollCarousel eyebrow="Best Deals" title={`Best Value ${stripBestPrefix(hub.title)}`} bgClass="bg-gray-50">
          {bestValueProducts.map((p) => (
            <ProductCarouselCard key={p.id} product={toCarouselProduct(p)} />
          ))}
        </ScrollCarousel>
      )}

      <div className={`${PAGE_W} py-4`}>
        <ProductShareButtons productTitle={hub.title} pageType="guide" variant="bottom" />
      </div>

      <div className={PAGE_W}>
        {articles.length > 0 && (
          <section className="mb-12">
            <PageSectionHeader eyebrow="Latest Articles" title={`${stripBestPrefix(hub.title)} Articles`} actions={<Link href="/blogs/news" className="tap-24 text-sm font-medium text-[#0469f0] hover:underline flex items-center gap-1">All Articles<svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 5l7 7-7 7" /></svg></Link>} />
            <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-4">
              {articles.map((article) => (
                <ArticleCard key={article.id} imageUrl={article.image || undefined} title={article.title} author={article.author_name || "Fridge.com"} authorImageUrl={article.authorImageUrl ?? null} date={formatDate(article.published_at)} excerpt={buildExcerpt(article.content || '', article.title)} href={`/blogs/news/${article.slug}`} />
              ))}
            </div>
          </section>
        )}

        {hub.tools_section && hub.tools_section.length > 0 && (
          <section className="mb-12">
            <PageSectionHeader eyebrow="Interactive Tools" title="Helpful Tools" />
            <div className="grid grid-cols-1 md:grid-cols-3 gap-3">
              {hub.tools_section.map((tool) => (<ToolLinkCard key={tool.href} title={tool.title} description={tool.description} href={tool.href} />))}
            </div>
          </section>
        )}

        {hub.cta_title && (
          <section className="relative rounded-2xl overflow-hidden mb-12">
            <OptimizedImg src="https://images.unsplash.com/photo-1541604193435-22287d32c2c2?w=1200&q=80" alt="" fill sizes="(min-width: 1280px) 1216px, 100vw" className="object-cover" />
            <div className="absolute inset-0 bg-black/65" />
            <div className="relative z-10 text-center py-16 px-10">
              <h2 className="text-2xl md:text-3xl font-black tracking-tight text-white mb-3 drop-shadow-lg">{hub.cta_title}</h2>
              {hub.cta_description && <p className="text-white/80 text-sm mb-6 max-w-xl mx-auto">{hub.cta_description}</p>}
              {(hub.cta_button_link || collectionHref) ? (<Link href={(hub.cta_button_link || collectionHref) as string} className="inline-flex items-center gap-2 bg-[#ff9951] hover:bg-[#f08030] text-[var(--color-brand-action-text)] font-semibold px-7 py-3 rounded-lg transition-colors text-sm shadow-lg">
                {hub.cta_button_text || `Browse ${hub.title}`}
                <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 5l7 7-7 7" /></svg>
              </Link>) : null}
            </div>
          </section>
        )}

      </div>
    </div>
  )
}
